import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { first, map, switchMap, take, takeUntil } from 'rxjs/operators';

import {
  DevicesStore,
  EnergyHistoricalDataFeaturesGroup,
  EnergyHistoricalDataStore,
  hemsPropertiesUnits,
  SelectableFeature,
  SelectableFeaturesGroup,
} from '@store';
import { combineLatest, Observable, of, Subject } from 'rxjs';

import {
  FeatureNames,
  HemsProperties,
  powerChartFeatures,
  thermalChartFeatures,
} from '@vi-iot/client';

import { isFeatureOnForbiddenListForGivenFilter } from '../../../../@store/energy-historical-data/energy-historical-data.helpers';

import { ActivatedRoute } from '@angular/router';
import { FilterType } from '../energy-filters-header/energy-filters.data';
import { DevicesService } from 'src/app/@store/devices/devices.service';

@Component({
  selector: 'app-chart-parameters-popup',
  templateUrl: './chart-parameters-popup.component.html',
  styleUrls: ['./chart-parameters-popup.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChartParametersPopupComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  readonly MAX_DISPLAYED_FEATURES = 6;

  @Input() isThermalFlow = false;
  @Output() popupClosed = new EventEmitter();

  chartFeatures: EnergyHistoricalDataFeaturesGroup[];
  selectableFeaturesGroups: SelectableFeaturesGroup[];
  propertiesUnits = hemsPropertiesUnits;
  parametersSelected = 1;
  unitsSelected = 1;
  installationId: string;

  activeFilterType: FilterType;
  listenToClickEvents = false;

  @HostListener('document:click', ['$event.target'])
  public onPageClick(targetElement): void {
    if (!this.listenToClickEvents) {
      return;
    }

    const clickedOutside = !this.elementRef.nativeElement.contains(targetElement);
    if (clickedOutside) {
      this.onClose();
    }
  }

  private ngUnsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    private energyHistoricalDataStore: EnergyHistoricalDataStore,
    private translateService: TranslateService,
    private elementRef: ElementRef,
    private route: ActivatedRoute,
    private devicesStore: DevicesStore,
    private devicesService: DevicesService
  ) {}

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntil(this.ngUnsubscribe$)).subscribe((params) => {
      this.installationId = params.get('id');
    });
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.listenToClickEvents = true;
    });
  }

  ngOnChanges(): void {
    this.energyHistoricalDataStore.filterType$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((filterType) => (this.activeFilterType = filterType));

    this.chartFeatures = this.isThermalFlow
      ? structuredClone(thermalChartFeatures)
      : structuredClone(powerChartFeatures);
    this.createSelectableFeatures();
  }

  initSelectedFeatures(): void {
    this.energyHistoricalDataStore.selectedFeatures$.pipe(first()).subscribe((selectedFeatures) => {
      selectedFeatures.forEach((entry) => {
        this.selectableFeaturesGroups.forEach((featureGroup) => {
          const foundFeature = featureGroup.features.find((feature) => feature.name === entry);

          if (foundFeature) {
            foundFeature.checked = true;
          }
        });
      });
    });
  }

  toggleFeatureSelection(feature: SelectableFeature): void {
    feature.checked = !feature.checked;
  }

  isCheckboxDisabled(feature: SelectableFeature): boolean {
    let selectedFeatures = this.getSelectedFeatures();

    const maximumUniqueUnitsLimitReached = this.isMaximumUniqueUnitsLimitReached(
      feature,
      selectedFeatures
    );
    const maximumFeaturesSelected = this.isMaximumFeaturesLimitReached(selectedFeatures);
    const checkboxNotAlreadyChecked = !feature.checked;
    const isForbiddenFeature = isFeatureOnForbiddenListForGivenFilter(
      feature.name,
      this.activeFilterType
    );

    return (
      (maximumFeaturesSelected && checkboxNotAlreadyChecked) ||
      maximumUniqueUnitsLimitReached ||
      isForbiddenFeature
    );
  }

  onApplyParameters(): void {
    let selectedFeatures = [];

    this.selectableFeaturesGroups.forEach((featuresGroup) => {
      const checkedFeatures = featuresGroup.features
        .filter((entry) => entry.checked)
        .map((entry) => entry.name);
      selectedFeatures = selectedFeatures.concat(checkedFeatures);
    });

    this.energyHistoricalDataStore.updateSelectedFeatures(selectedFeatures);
    this.energyHistoricalDataStore.saveSelectedFeaturesToStorage(
      this.installationId,
      this.isThermalFlow,
      selectedFeatures
    );

    this.popupClosed.emit();
  }

  onClose(): void {
    this.popupClosed.emit();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  private getSelectedFeatures(): SelectableFeature[] {
    let selectedFeatures: SelectableFeature[] = [];

    this.selectableFeaturesGroups.forEach((featuresGroup) => {
      const checkedFeatures = featuresGroup.features.filter((entry) => entry.checked);
      selectedFeatures = selectedFeatures.concat(checkedFeatures);
    });

    return selectedFeatures;
  }

  private initSummaryParametersCounters(): void {
    const selectedFeatures = this.getSelectedFeatures();
    this.parametersSelected = selectedFeatures.length;
    this.unitsSelected = [...new Set(selectedFeatures.map((feature) => feature.unit))].length;
  }

  private isMaximumFeaturesLimitReached(selectedFeatures: SelectableFeature[]): boolean {
    this.parametersSelected = selectedFeatures.length;

    return selectedFeatures.length === this.MAX_DISPLAYED_FEATURES;
  }

  private isMaximumUniqueUnitsLimitReached(
    feature: SelectableFeature,
    selectedFeatures: SelectableFeature[]
  ): boolean {
    const featureUnit = this.propertiesUnits.find((entry) => entry.property === feature.name)?.unit;
    const uniqueUnitsSelected = [];

    selectedFeatures.forEach((singleFeature) => {
      const entryUnit = this.propertiesUnits.find((entry) => entry.property === singleFeature.name)
        ?.unit;

      if (!uniqueUnitsSelected.includes(entryUnit)) {
        uniqueUnitsSelected.push(entryUnit);
      }
    });

    const isFeatureUnitWithinAlreadySelectedUnits = uniqueUnitsSelected.includes(featureUnit);
    const maximumUniqueUnitsLimitReached = uniqueUnitsSelected.length > 1;
    this.unitsSelected = uniqueUnitsSelected.length;

    return !isFeatureUnitWithinAlreadySelectedUnits && maximumUniqueUnitsLimitReached;
  }

  private createSelectableFeatures(): void {
    const busAdressesWithDeviceName$: Observable<
      { name: string; busAddress: string }[]
    > = this.devicesStore.wallboxIndexedPowerFeatures$.pipe(
      switchMap((features) => {
        if (!features.length) {
          return of([] as { name: string; busAddress: string }[]);
        }

        return combineLatest(
          features.map((feature) =>
            this.devicesService
              .getOBNGWallboxDeviceNameByBusAddress(feature.busAddress)
              .pipe(map((name) => ({ name, busAddress: feature.busAddress })))
          )
        );
      })
    );

    const isOBNG$ = this.devicesStore.isOBNGWallboxPresent$;

    const dcgDevicesWithName$: Observable<
      { name: string; index: string }[]
    > = this.devicesStore.dryContactGeneric$.pipe(
      switchMap((devices) => {
        if (!devices.length) {
          return of([] as { name: string; index: string }[]);
        }

        const mappedDevices = devices
          .map((device) => ({ device, index: device.id.split('-').pop() }))
          .sort((a, b) => Number(a.index) - Number(b.index))
          .map(({ device, index }) =>
            this.devicesService
              .getDryContactGenericDeviceName(device)
              .pipe(map((name) => ({ name, index })))
          );

        return combineLatest(mappedDevices);
      })
    );

    combineLatest([busAdressesWithDeviceName$, isOBNG$, dcgDevicesWithName$])
      .pipe(take(1), takeUntil(this.ngUnsubscribe$))
      .subscribe(([busAdressesWithDeviceName, isOBNG, dcgDevicesWithName]) => {
        let featuresGroups: SelectableFeaturesGroup[] = [];
        this.chartFeatures.forEach((featuresGroup) => {
          const mappedGroup: SelectableFeaturesGroup = {
            groupKey: featuresGroup.groupKey,
            features: [],
          };

          // Handle additional wallbox devices
          if (!this.isThermalFlow && isOBNG) {
            const indexOfVCSDevice = featuresGroup.features.findIndex(
              (el) => el.feature === FeatureNames.emsEnergyConsumptionVcs
            );

            if (indexOfVCSDevice >= 0) {
              const firstIndexVCS = busAdressesWithDeviceName[0].busAddress;
              const vscEntry = featuresGroup.features[indexOfVCSDevice];
              vscEntry.feature = FeatureNames.emsEnergyConsumptionVcsWithIndex(
                firstIndexVCS
              ) as HemsProperties;
              // Extend with name basing on feature's value
              vscEntry.name = busAdressesWithDeviceName[0].name;

              // Add wallbox devices and match their feature
              busAdressesWithDeviceName.slice(1).forEach((el, index) => {
                featuresGroup.features.splice(indexOfVCSDevice + index, 0, {
                  ...vscEntry,
                  feature: FeatureNames.emsEnergyConsumptionVcsWithIndex(
                    el.busAddress
                  ) as HemsProperties,
                  name: el.name,
                });
              });

              // Sort wallbox by bus adress
              featuresGroup.features.sort((a, b) => {
                const feature1 = a.feature;
                const feature2 = b.feature;
                if (
                  feature1.includes('ems.energy.consumption.vcs.') &&
                  feature1.includes('ems.energy.consumption.vcs.')
                ) {
                  const feature1Segments = feature1.split('.');
                  const feature1BusAddress = feature1Segments[feature1Segments.length - 1];
                  const feature2Segments = feature2.split('.');
                  const feature2BusAddress = feature2Segments[feature2Segments.length - 1];

                  return Number(feature1BusAddress) - Number(feature2BusAddress);
                } else {
                  return 0;
                }
              });
            }
          }

          // Handle Dry Contact Generic (DCG) devices
          if (
            !this.isThermalFlow &&
            dcgDevicesWithName.length &&
            featuresGroup.groupKey === '+HEMS.PARAMETERS_POPUP.GROUP_CONSUMER'
          ) {
            dcgDevicesWithName.forEach((dcgDevice) => {
              featuresGroup.features.push({
                feature: FeatureNames.emsEnergyConsumptionOtherWithIndex(
                  dcgDevice.index
                ) as HemsProperties,
                name: dcgDevice.name,
              });
            });
          }

          mappedGroup.features = featuresGroup.features.map((feature) => {
            return {
              name: feature.feature,
              displayName:
                feature.name ||
                this.translateService.instant(`+HEMS_FEATURES.${feature.feature.toUpperCase()}`),
              checked: false,
              unit: feature.unit,
            };
          });

          featuresGroups.push(mappedGroup);
        });

        this.selectableFeaturesGroups = featuresGroups;

        this.initSelectedFeatures();
        this.initSummaryParametersCounters();
      });
  }
}
